/**
 * Managed client effort (BUILD_PLAN.md section 13.2; ADR 0146 decision 4):
 * which flat-rate clients take the most effort. Each managed client's hours
 * by calendar month of the filters' days, captured and logged side by side
 * (unlogged work is effort too), the busiest first, as the API counts them:
 * the day view's time for each block, across the techs, on each tech's own
 * local day. A client with no work is listed too. Under each client's name,
 * its tickets open now, of the report's techs (ADR 0185): as of now, not of
 * the days.
 *
 * "Download CSV" saves the same rows.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { useId } from "react";

import type { EffortPeriod, ManagedEffort } from "../../api/types";
import { useSession } from "../../app/session-context";
import { HipaaBadge } from "../../components/ui/Badge";
import { Panel } from "../../components/ui/Panel";
import { addDays, formatDayParts } from "../../lib/dates";
import { saveBlob } from "../../lib/download";
import { formatDuration } from "../../lib/durations";
import { count, errorMessage, pillButton, retryOnce } from "./common";
import { useReportFilters } from "./filters";

const headCell = "pb-2 pr-3";

/** "October 2026" for a whole month; "Sep 15 – Sep 30" for part of one. */
function monthLabel(period: EffortPeriod): string {
  const [y, m] = period.start.split("-").map(Number) as [number, number];
  const whole = period.start.endsWith("-01") && addDays(period.end, 1).endsWith("-01");
  if (whole) {
    return new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric", timeZone: "UTC" }).format(
      new Date(Date.UTC(y, m - 1, 1)),
    );
  }
  return `${formatDayParts(period.start).date} – ${formatDayParts(period.end).date}`;
}

/** Captured over logged, in one cell. */
function Hours({ period, strong = false }: { period: EffortPeriod; strong?: boolean }) {
  return (
    <span className="flex flex-col">
      <span className={strong ? "font-semibold tracking-[-0.02em]" : "font-medium"}>
        {formatDuration(period.captured_seconds)}
        <span className="text-xs font-normal text-muted"> captured</span>
      </span>
      <span className="text-xs text-muted">
        {formatDuration(period.logged_seconds)}
        <span> logged</span>
      </span>
    </span>
  );
}

function EffortTable({ report }: { report: ManagedEffort }) {
  const months = report.clients[0]?.months ?? [];
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-[13.5px]">
        <caption className="sr-only">Each managed client&apos;s hours by month</caption>
        <thead className="text-[12.5px] font-medium tracking-[0.08em] text-muted uppercase">
          <tr>
            <th scope="col" className={headCell}>
              Client
            </th>
            {months.map((month) => (
              <th key={month.start} scope="col" className={headCell}>
                {monthLabel(month)}
              </th>
            ))}
            <th scope="col" className="pb-2">
              Total
            </th>
          </tr>
        </thead>
        <tbody>
          {report.clients.map((effort) => (
            <tr key={effort.client.id} className="border-t border-line align-top">
              <th scope="row" className="py-3 pr-3 font-medium">
                <span className="flex flex-col gap-1">
                  <span>{effort.client.name}</span>
                  {effort.client.is_hipaa ? <HipaaBadge /> : null}
                  {/* As of now, not of the days (ADR 0185). */}
                  <span className="text-xs font-normal text-muted">
                    {`${count(effort.open_tickets, "open ticket", "open tickets")} now`}
                  </span>
                </span>
              </th>
              {effort.months.map((month) => (
                <td key={month.start} className="py-3 pr-3 whitespace-nowrap">
                  <Hours period={month} />
                </td>
              ))}
              <td className="py-3 whitespace-nowrap">
                <Hours period={effort.total} strong />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ManagedEffortReport() {
  const { api } = useSession();
  const headingId = useId();
  const [filters] = useReportFilters();
  const report = useQuery({
    queryKey: ["reports", "managed-effort", filters],
    queryFn: () => api.getManagedEffort(filters),
    retry: retryOnce,
  });
  const download = useMutation({
    mutationFn: () => api.downloadManagedEffortCsv(filters),
    onSuccess: (blob) => {
      saveBlob(blob, `managed-effort-${filters.start}-to-${filters.end}.csv`);
    },
  });

  return (
    <Panel aria-labelledby={headingId} className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h2 id={headingId} className="text-lg font-semibold tracking-tight">
            Managed client effort
          </h2>
          <p className="text-[13.5px] text-muted">
            Each managed client&apos;s hours by month, captured and logged, the busiest first. Captured is every
            tech&apos;s time for the client, logged or not. Open tickets are as of now.
          </p>
        </div>
        <button
          type="button"
          className={pillButton}
          disabled={!report.isSuccess || download.isPending}
          onClick={() => {
            download.mutate();
          }}
        >
          Download CSV
        </button>
      </div>
      {download.isError ? (
        <p role="alert" className="text-sm text-status-needs-client-fg">
          Not downloaded: {errorMessage(download.error)}
        </p>
      ) : null}
      {report.isPending ? (
        <p role="status" className="text-muted">
          Loading the report…
        </p>
      ) : report.isError ? (
        <p role="alert" className="text-status-needs-client-fg">
          {errorMessage(report.error)}
        </p>
      ) : report.data.clients.length === 0 ? (
        <p className="py-6 text-center text-muted">No managed clients to report on.</p>
      ) : (
        <EffortTable report={report.data} />
      )}
    </Panel>
  );
}
